/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CameraResultType, ImageOptions, Photo } from '@capacitor/camera';
import { LoadingController, Platform } from '@ionic/angular';
import { ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';
import { Subscription } from 'rxjs';
import { CameraService } from 'src/app/services/camera.service';
import { DataProviderService } from 'src/app/services/data-provider.service';
import { QuestionnaireSharedService } from 'src/app/services/questionnaire-shared.service';
import { TranslatePipe } from '@ngx-translate/core';
import { BackButtonService } from 'src/app/services/back-button.service';

@Component({
  selector: 'app-question-photos',
  templateUrl: './question-photos.page.html',
  styleUrls: ['./question-photos.page.scss'],
})
export class QuestionPhotosPage implements OnInit, OnDestroy {
  answerId: number;
  id: number;
  parentObject: string;
  customFormId: number;
  maxNumberOfPhotos: number;
  photosCount: number;
  categoryKey: string;
  question: string;
  photos: any[];
  questionObject: any;
  routerState: any;
  savingProcess = false;
  noInternetConnection = false;
  editable = true;

  imageChangedEvent: any = '';
  remarks = '';
  annex = false;
  photoView = false;
  photoUpdateView = false;
  chosenPicture: any = '';
  updatePicture: any;
  croppedImage: any = '';
  landscape = false;
  aspectRatio = '';
  private loadingDialog: HTMLIonLoadingElement;
  private backButtonSubscription: Subscription;

  constructor(
    private dataService: DataProviderService,
    private route: ActivatedRoute,
    private router: Router,
    private camera: CameraService,
    private loadingController: LoadingController,
    private questionnaireSharedService: QuestionnaireSharedService,
    private platform: Platform,
    private translatePipe: TranslatePipe,
    private backButtonService: BackButtonService
  ) {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.navigateToQuestionnairePage();
      },
    });
  }
  ionViewWillEnter(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.navigateToQuestionnairePage();
      },
    });
  }
  ngOnInit(): void {
    this.backButtonSubscription =
      this.platform.backButton.subscribeWithPriority(10, () => {
        this.navigateToQuestionnairePage();
      });
    this.route.params.subscribe((params) => {
      this.answerId = parseInt(params?.answerId, 10);
      this.maxNumberOfPhotos = parseInt(params?.maxNumberOfPhotos, 10);
      this.categoryKey = params?.categoryKey;
      this.question = params?.question;
      this.photosCount = parseInt(params?.photosCount, 10);
      this.id = parseInt(params?.id, 10);
      this.parentObject = params?.parentObject;
      this.customFormId = parseInt(params?.customFormId, 10);
      this.routerState = this.router.getCurrentNavigation().extras.state;
      this.editable = this.routerState?.editable ?? true;
      this.getQuestionPhotos(false);
    });
  }

  async getQuestionPhotos(changeQuestion: boolean) {
    await this.showLoading();
    this.dataService.getPhotos(this.parentObject, this.answerId).subscribe(
      (result) => {
        if (result !== 0) {
          this.noInternetConnection = false;
          this.photos = result;
          this.loadingDialog.dismiss();
          if (changeQuestion) {
            this.changeQuestionnaireObject();
          }
        } else {
          this.loadingDialog.dismiss();
          this.photos = [];
          this.noInternetConnection = true;
        }
      },
      (error) => {
        this.loadingDialog.dismiss();
      },
      () => {}
    );
  }

  cancel() {
    this.photoUpdateView = false;
    this.photoView = false;
  }
  handleUpdatePhoto(photo: any) {
    this.photoUpdateView = true;
    this.updatePicture = photo;
  }

  async handleTakePhoto() {
    const options: ImageOptions = {
      quality: 90,
      resultType: CameraResultType.DataUrl,
      correctOrientation: true,
      allowEditing: false,
      height: 800,
      width: 800,
      saveToGallery: true,
      promptLabelPhoto: 'From Gallery',
    };

    this.camera.getPhoto(options).then((imageData: Photo) => {
      this.getPhotoMetaData(imageData.dataUrl).then((result: any) => {
        if (result.width > result.height) {
          this.landscape = true;
          this.aspectRatio = '4 / 3';
        } else {
          this.landscape = false;
          this.aspectRatio = '3 / 4';
        }

        this.chosenPicture = imageData.dataUrl;
        this.photoView = true;
      });
    });
  }

  savePhoto() {
    this.savingProcess = true;
    this.photosCount = this.photosCount + 1;

    this.dataService
      .savePhoto(this.parentObject, this.answerId, {
        Description: this.remarks,
        Picture: this.croppedImage,
        Landscape: this.landscape,
        Annex: this.annex,
      })
      .then((result) => {
        this.photos.push({
          id: result.id,
          picture: this.croppedImage,
          description: this.remarks,
        });
        this.remarks = '';
        this.photoView = false;
        this.savingProcess = false;

        this.changeQuestionnaireObject();
      })
      .catch((error) => {
        this.savingProcess = false;
        console.error(`save photo error: ${error.message}`);
      });
  }

  updatePhoto(Id: number) {
    this.savingProcess = true;

    this.dataService
      .updatePhoto(this.parentObject, Id, {
        Description: this.updatePicture.description,
        Annex: this.updatePicture.annex,
      })
      .then((result) => {
        this.remarks = '';
        this.photoUpdateView = false;
        this.savingProcess = false;
      })
      .catch((error) => {
        this.savingProcess = false;
        console.error(`save photo error: ${error.message}`);
      });
  }

  deletePhoto(photoId: number) {
    this.photosCount = this.photosCount - 1;
    this.dataService
      .deletePhoto(this.parentObject, photoId)
      .then((result) => {
        this.photos = this.photos.filter((photo) => photo.id !== photoId);
        this.changeQuestionnaireObject();
      })

      .catch((error) => {
        console.error(`delete photo error: ${error.message}`);
      });
  }
  async showLoading() {
    this.loadingDialog = await this.loadingController.create({
      message: this.translatePipe.transform('LOADING'),
    });

    this.loadingDialog.present();
  }

  changeQuestionnaireObject() {
    const changeObject = {
      photosCount: this.photosCount,
      answerId: this.answerId,
      categoryKey: this.categoryKey,
      id: this.id,
    };

    this.questionnaireSharedService.onNewPhotosEvent.emit(changeObject);
  }

  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;
  }
  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }
  imageLoaded(image: LoadedImage) {}
  cropperReady() {}
  loadImageFailed() {}

  async getPhotoMetaData(base64Str) {
    return new Promise(async (resolve, reject) => {
      try {
        const str = base64Str.toLowerCase().trim();
        const regex = /data:image\/([a-z]{2,4});base64,([A-Za-z0-9\/+=]{1,})/i;

        if (!regex.test(str)) {
          reject('Invalid base64 encoded image string.');
        }

        const image = new Image();
        image.src = base64Str;
        image.onload = () => {
          resolve({
            width: image.width,
            height: image.height,
          });
        };
      } catch (error) {
        reject(error);
      }
    });
  }
  navigateToQuestionnairePage() {
    this.router.navigate(
      ['questionnaire', this.customFormId, this.parentObject, this.id],
      {
        state: this.routerState,
      }
    );
  }

  ngOnDestroy() {
    this.backButtonSubscription?.unsubscribe();
  }
}
